/**
 * Google Analytics 4 (gtag.js) + Consent Mode v2.
 *
 * We deliberately load GA4 directly via gtag.js rather than Google Tag Manager.
 * GTM would let arbitrary, tenant-controlled tags/scripts execute on our shared
 * multi-tenant domain — a script-injection risk. gtag.js only sends measurement
 * data to the configured GA4 property and runs no third-party script, which
 * keeps the site's strict CSP (`script-src` without `unsafe-inline`) meaningful.
 */

type GtagConsentValue = 'granted' | 'denied'

interface ConsentUpdate {
  ad_storage: GtagConsentValue
  ad_user_data: GtagConsentValue
  ad_personalization: GtagConsentValue
  analytics_storage: GtagConsentValue
}

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[]
  }
}

let ga4Initialised = false
let googleAdsSendTo: string | null = null

/** Deploy environments whose traffic should be inspectable in GA4 DebugView. */
const DEBUG_VIEW_ENVIRONMENTS = ['test', 'staging']

/**
 * GA4 DebugView only receives events when the tag opts in, and the browser
 * extension is not an option for QA on a deployed build.
 */
const isDebugViewEnvironment = (): boolean =>
  DEBUG_VIEW_ENVIRONMENTS.includes(
    import.meta.env.VITE_ENVIRONMENT_NAME_LOWERCASE
  )

/** Low-level gtag pusher (Consent Mode expects the raw arguments object). */
function gtag(command: string, ...rest: unknown[]): void {
  void command
  void rest
  window.dataLayer = window.dataLayer || []
  // eslint-disable-next-line prefer-rest-params -- gtag/Consent Mode requires the raw `arguments` object, not a rest array
  window.dataLayer.push(arguments as unknown as Record<string, unknown>)
}

/** Consent Mode v2 default — deny all storage until the customer opts in. */
const setConsentDefault = (): void => {
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    wait_for_update: 500,
  })
}

/**
 * Bootstraps GA4 for this marketplace's measurement id via gtag.js, with
 * Consent Mode v2 defaulting to denied. Injects the external gtag.js script
 * (not an inline snippet) so the strict CSP stays intact. No-op when the
 * marketplace has no GA4 id configured, or if GA4 is already initialised.
 */
export const initGa4 = (ga4MeasurementId: string | null | undefined): void => {
  if (ga4Initialised || !ga4MeasurementId) return
  ga4Initialised = true
  window.dataLayer = window.dataLayer || []
  setConsentDefault()
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${ga4MeasurementId}`
  document.head.appendChild(script)
  gtag('js', new Date())
  gtag(
    'config',
    ga4MeasurementId,
    isDebugViewEnvironment() ? { debug_mode: true } : {}
  )
}

/**
 * Reflects the customer's cookie choice into Consent Mode v2.
 * Granting unlocks GA4 measurement and Google Ads (incl. Enhanced Conversions).
 */
export const updateConsent = (granted: boolean): void => {
  const value: GtagConsentValue = granted ? 'granted' : 'denied'
  const update: ConsentUpdate = {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value,
  }
  gtag('consent', 'update', update)
}

/** Sends a GA4 event via gtag. No-op until GA4 has been initialised. */
export const trackGa4Event = (
  event: string,
  params: Record<string, unknown>
): void => {
  if (!ga4Initialised) return
  gtag('event', event, params)
}

/**
 * Sets hashed first-party user data for Google Ads Enhanced Conversions.
 * No-op until GA4 has been initialised.
 */
export const setGa4UserData = (userData: Record<string, unknown>): void => {
  if (!ga4Initialised) return
  gtag('set', 'user_data', userData)
}

/**
 * Configures the marketplace's Google Ads tag on the shared gtag instance.
 *
 * Enhanced Conversions is a Google Ads feature, so the hashed customer data set
 * by `setGa4UserData` only reaches Google once a Google Ads tag is configured
 * and a conversion fires against it. Needs the conversion label as well as the
 * id, because `send_to` addresses a specific conversion action.
 */
export const initGoogleAds = (
  googleAdsId: string | null | undefined,
  purchaseLabel: string | null | undefined
): void => {
  if (googleAdsSendTo || !googleAdsId || !purchaseLabel) return
  googleAdsSendTo = `${googleAdsId}/${purchaseLabel}`
  gtag('config', googleAdsId)
}

/**
 * Reports a completed order as a Google Ads conversion. No-op unless the
 * marketplace has both a Google Ads id and a purchase label configured.
 */
export const trackGoogleAdsPurchase = (params: {
  value?: number
  currency?: string
  transactionId?: string
}): void => {
  if (!googleAdsSendTo) return
  gtag('event', 'conversion', {
    send_to: googleAdsSendTo,
    value: params.value,
    currency: params.currency,
    transaction_id: params.transactionId,
  })
}
